// Well Workspace POC — client types. Field names are camelCase to match the
// jbuilder key format (the API auto-camelizes).
import { capitalizeFirstLetter } from 'utils/strings';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';

// `proposal`, `planned`, `pre_permit` and `apd` are the NON-DERIVED statuses (WellStatusCalculator::
// NON_DERIVED_STATUSES): nothing ingested ever computes them, and only a well authored
// through Create New Well carries one. A created well is seeded `pre_permit`, so without
// them here its own status renders unlabelled and uncoloured.
export type WellStatusToken =
  | 'producing'
  | 'shut_in'
  | 'duc'
  | 'permit'
  | 'expired_permit'
  | 'inactive'
  | 'proposal'
  | 'planned'
  | 'pre_permit'
  | 'apd';

// Verbatim from the shared vocabulary (app/shared/enums/well_status_types.json), which is
// what `wellStatusLabel` reads and what the backend stores.
export const WELL_STATUS_LABELS: Record<WellStatusToken, string> = {
  producing: 'Producing',
  shut_in: 'Shut In',
  duc: 'DUC',
  permit: 'Permit',
  expired_permit: 'Expired Permit',
  inactive: 'Inactive',
  proposal: 'Proposal',
  planned: 'Planned',
  pre_permit: 'Pre-Permit',
  apd: 'APD',
};

/**
 * A stored status as the workspace surfaces render it: the vocabulary's label, the raw token
 * when the vocabulary has outgrown this map, and an em dash when there is no status at all.
 *
 * Not `utils/well-status.ts`'s `wellStatusLabel`, which answers '' for an absent status and
 * reads the FULL backend vocabulary — its callers each bolt their own dash on, and the colour
 * map beside this one is keyed on the narrow token union, so widening the labels here would
 * leave newly-labelled statuses uncoloured.
 * @param {string | null | undefined} status - The stored status.
 * @return {string} Its label, the raw value, or an em dash.
 */
export const wellStatusLabelOrDash = (
  status: string | null | undefined
): string => {
  if (!status) return '—';

  return WELL_STATUS_LABELS[status as WellStatusToken] ?? status;
};

/**
 * The Wellbore Type as the Spacing table and its export show it: the raw well type
 * proper-cased ('permit' → 'Permit', 'sidetrack' → 'Sidetrack'), blank with none.
 * @param {string | null | undefined} type - The offset's raw well type.
 * @return {string} Its label.
 */
export const geometryTypeLabel = (type: string | null | undefined): string =>
  type ? capitalizeFirstLetter(type) : '';

// Dot colors — aligned with the mock's STATUS_COLORS. The four pre-permit states are
// sibling shades of one hue because they are adjacent lifecycle stages of the same thing
// (proposed, planned, no permit filed, then filed and pending); the mock publishes no tokens for them, so the
// hexes are Carbon's purple ramp and are safe to re-tune against the final design.
// Read from the shared colour file (utils/well-status WELL_STATUS_COLOR_MAP, INS-13300).
export const WELL_STATUS_COLORS: Record<WellStatusToken, string> = {
  permit: WELL_STATUS_COLOR_MAP.permit,
  duc: WELL_STATUS_COLOR_MAP.duc,
  producing: WELL_STATUS_COLOR_MAP.producing,
  shut_in: WELL_STATUS_COLOR_MAP.shut_in,
  expired_permit: WELL_STATUS_COLOR_MAP.expired_permit,
  inactive: WELL_STATUS_COLOR_MAP.inactive,
  proposal: WELL_STATUS_COLOR_MAP.proposal,
  planned: WELL_STATUS_COLOR_MAP.planned,
  pre_permit: WELL_STATUS_COLOR_MAP.pre_permit,
  apd: WELL_STATUS_COLOR_MAP.apd,
};

// The staged lifecycle date fields, in chronological order.
export const DATE_ORDER = [
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
  'firstProductionDate',
  'lastProductionDate',
] as const;
export type StagedDateField = (typeof DATE_ORDER)[number];

export const DATE_FIELD_LABELS: Record<StagedDateField, string> = {
  permitDate: 'Permit Date',
  spudDate: 'Spud Date',
  rigReleaseDate: 'Rig Release Date',
  completionDate: 'Completion Date',
  firstProductionDate: 'First Production Date',
  lastProductionDate: 'Last Production Date',
};

// ── Well Status editing (Epic 2, Story 2) ──
// The set the backend accepts, in lifecycle order, as the edit dropdown lists them.
// Mirrors WellStatusCalculator::STATUSES.
//
// Proposal, Planned, Pre-Permit and APD lead rather than trail: they are the stages BEFORE a
// permit is approved (proposed, planned, nothing filed, then filed and pending), so lifecycle
// order puts them ahead of Permit. They were excluded until Epic 3 Story 1, when the backend made both real tokens.
export const WELL_STATUS_ORDER: WellStatusToken[] = [
  'proposal',
  'planned',
  'pre_permit',
  'apd',
  'permit',
  'duc',
  'producing',
  'shut_in',
  'inactive',
  'expired_permit',
];

// The dates an operator may WRITE, mirroring StagedWell::EDITABLE_STATUS_FIELDS minus
// `status`. Chronological, which is also the order the server enforces them in.
export const EDITABLE_DATE_FIELDS = [
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
] as const;
export type EditableDateField = (typeof EDITABLE_DATE_FIELDS)[number];

// camelCase form field -> the snake_case key the PATCH body takes. `firstProductionDate`
// is sent only for a well the server will accept it for (no production records — see
// canEditFirstProductionDate); for any other well it is derived read-only and the server
// slices it back out, so the panel omits it rather than send a value it cannot change.
export const FIELD_TO_SERVER_KEY: Record<
  'status' | EditableDateField | 'firstProductionDate',
  string
> = {
  status: 'status',
  permitDate: 'permit_date',
  spudDate: 'spud_date',
  rigReleaseDate: 'rig_release_date',
  completionDate: 'completion_date',
  firstProductionDate: 'first_production_date',
};

// The core keys every edit sends, whatever the well. `firstProductionDate` is conditional
// and therefore deliberately absent.
export const ALWAYS_SENT_FIELDS = ['status', ...EDITABLE_DATE_FIELDS] as const;

// Required lifecycle dates by status (PRD Table 3), mirroring
// StagedWell::REQUIRED_DATES_BY_STATUS. Statuses not listed require none.
export const REQUIRED_DATES_BY_STATUS: Partial<
  Record<WellStatusToken, StagedDateField[]>
> = {
  permit: ['permitDate'],
  duc: ['spudDate', 'permitDate'],
  producing: ['firstProductionDate', 'spudDate', 'permitDate'],
};

// The per-well Well-Status freeze verdict. `correct` on a PROMOTED workspace is what
// freezes the well's status against system ingest; the other three do not freeze.
export type Verdict = 'unanswered' | 'correct' | 'incorrect' | 'na';

// The event kinds the shared verification_items events endpoint takes.
export const VERDICT_TO_KIND = {
  correct: 'verification_correct',
  incorrect: 'verification_incorrect',
  unanswered: 'verification_reverted',
  na: 'verification_na',
} as const;

export interface WellStatusVerdict {
  verificationItemId: number;
  verdict: Verdict;
}

// One changed field, as the backend emits it. An array entry rather than a hash keyed
// on the field name, because jbuilder's `key_format` does not deep-format nested hash
// values. So `field` is a reliable snake_case string (`current_operator`).
export interface FieldChange {
  field: string;
  from: string | null;
  to: string | null;
}

// The canonical (ORIGIN) values a staged well is compared against. Epic 1 has no user
// editing, so these diverge only where system ingest changed something.
export interface StagedWellBaseline {
  status: WellStatusToken | null;
  permitDate: string | null;
  spudDate: string | null;
  rigReleaseDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  lastProductionDate: string | null;
}

// Epic-2 note: the verdict enum, the gate task, the freeze item and their label maps
// lived here until the 2026-09-03 re-scope, when the backend un-gated Promote and
// dropped those fields from the JSON. The design is preserved on
// INS-11687-well-workspace-epic2-foundation.

export interface StagedWell {
  /**
   * The staged row's own id — the stable address for a well authored in the workspace, which
   * has no meaningful uwi of its own (the backend generates a synthetic one), and the key
   * `PATCH /workspaces/:id/wells/:id` and the unit-candidate endpoints take.
   *
   * Null for a member well with no staged row yet: the detail builds a transient snapshot for
   * those, and they are addressed by uwi until their first edit materializes a row.
   */
  id: number | null;
  uwi: string;
  /** Null until the well is promoted — a well authored here has no canonical twin. */
  wellId: number | null;
  /**
   * The DERIVED Well Name (lease name + well number; there is no name column).
   *
   * Null for a well authored here that nobody has named yet. An existing well with no derived
   * name falls back to its uwi server-side, so a null here means "authored and unnamed" rather
   * than "unknown" — the synthetic backend uwi is deliberately never surfaced as a name, which
   * would contradict the promote gate reporting the name as missing. Read through
   * {@link wellDisplayName} rather than directly.
   */
  name: string | null;
  /** Operator-editable. One of the two fields the Well Name derives from. */
  leaseName: string | null;
  /** Operator-editable. The other. */
  wellNumber: string | null;
  status: WellStatusToken | null;
  permitDate: string | null;
  spudDate: string | null;
  rigReleaseDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  lastProductionDate: string | null;
  currentOperator: string | null;
  /**
   * The operator's entity page, when the catalog knows one. Served by
   * `_staged_well.json.jbuilder` as `current_operator_href` — the same link the Well page
   * and the map popup render.
   */
  currentOperatorHref?: string | null;
  holeDirection: string | null;
  srpLandingZone: string | null;
  vendorLandingZone: string | null;
  measuredDepthFeet: number | null;
  trueVerticalDepthFeet: number | null;
  stateAbbreviation: string | null;
  county: string | null;
  /**
   * The bore the well's geography, units and promoted line all derive from.
   *
   * ABSENT, not null, on a well that was not authored here — the backend renders the bore
   * block only for `new_well?`, since multi-bore belongs to the Create-New-Well flow. Null
   * (present) means authored but boreless: every bore has been removed.
   */
  primaryCompletionUwi14?: string | null;
  /** Every bore this well has drawn, lowest completion suffix first. Absent when not authored. */
  boreCompletionUwi14s?: string[];
  editedFields: string[]; // snake_case editable field names that diverge from canonical
  /**
   * The subset of `editedFields` a human actually staged: a new well's untouched system-seeded
   * defaults (name, number, status) are left out. Drives the per-field edited highlight only; the
   * "Edited" pills keep reading `editedFields`. Absent on a backend predating it, in which case
   * the highlight falls back to `editedFields`.
   */
  userEditedFields?: string[];
  baseline: StagedWellBaseline;
  // The Compare change-log: only the changed canonical-writable fields. Live while a
  // draft and frozen at Promote, so a promoted workspace still shows what it changed.
  // Absent on a backend predating Wave B.
  diff?: FieldChange[] | null;
  // The well's Well-Status freeze verdict (Epic 2). Absent until the freeze verification
  // item has been seeded, which the first status edit does — so a workspace nobody has
  // edited yet has no verdict to mark.
  wellStatusVerdict?: WellStatusVerdict | null;
}

export interface WellWorkspaceDetail {
  href: string;
  auth: string[];
  id: number;
  name: string;
  kind: 'property' | 'dca';
  promoted: boolean;
  promotedAt: string | null;
  promotedBy: string | null;
  promotable: boolean;
  /**
   * Why Promote is unavailable, in the backend's own words — one entry per missing piece
   * (`WellWorkspace#promotion_blockers`, the same list its 422 refusal carries). Read for the
   * button's tooltip: the button is disabled while a workspace is not promotable, so the
   * refusal path can never run and this is the only channel the reasons reach the reader by.
   *
   * Optional, because a backend predating it serves the envelope without — the tooltip then
   * falls back to the generic wording. Empty when the workspace is promotable.
   */
  promotionBlockers?: string[] | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Still on the shared envelope for Type Curve and IC; Epic 1 seeds no verification
  // items, so it reads 0/0 here and nothing renders it.
  tasks: { completed: number; total: number };
  wells: StagedWell[];
  /**
   * HAL links off the envelope. Shaped like the type-curve workspace's on purpose, so
   * the shared activity panel reads `events.href` / `comments.href` without a per-type
   * branch and nothing hand-builds a path. Optional because a backend predating them
   * serves the envelope without.
   */
  resources?: {
    promote?: { href: string; auth?: string[] };
    events?: { href: string };
    comments?: { href: string; auth?: string[] };
  };
}

// GET /workspaces/:id/compare — what this workspace changes against the canonical
// record. Only changed wells and fields appear, and the backend groups them into one
// section per dataset, naming the well on each row, so the section labels are its own.

/** One changed field of one well, ORIGIN (`from`) beside TARGET (`to`). */
export interface WorkspaceCompareRow {
  uwi: string;
  /** The well's display name, already resolved by the backend. */
  well: string;
  /** ISO date for a per-period row (production); null for a well-level field. */
  period: string | null;
  field: string;
  from: unknown;
  to: unknown;
  status: string;
  /** What the row does to the record: a brand-new record, or a change to an existing one. */
  action?: 'create' | 'edit';
  /** Whether the change has reached the wells, or is still staged in the workspace. */
  promoted?: 'promoted' | 'staged';
}

/** One dataset's changed rows, labelled by the backend. */
export interface WorkspaceCompareSection {
  key: string;
  label: string;
  rows: WorkspaceCompareRow[];
}

/** The counts the sheet's summary line reads, computed by the backend. */
export interface WorkspaceCompareRollup {
  wellsChanged: number;
  totalWells: number;
  edits: number;
}

export interface WorkspaceCompare {
  workspaceId: number;
  promoted: boolean;
  rollup: WorkspaceCompareRollup;
  sections: WorkspaceCompareSection[];
  /**
   * The workspace's linked documents, served once for the whole workspace. Kept for
   * the wire shape; the sheet reads the `related_documents` section instead, which has
   * one row per addition, removal and replacement.
   */
  documentsAdded: Array<{ id: number; kind: string; title: string }>;
}

export interface WellWorkspaceListItem {
  href: string;
  id: number;
  name: string;
  kind: 'property' | 'dca';
  wellsCount: number;
  /**
   * The extent of the workspace's wells' surface holes, `[minLng, minLat, maxLng,
   * maxLat]` in WGS84 — the wells query's `mapBounds` shape. Optional: the backend
   * is adding it; Map Query's "Show on map" is disabled on an item without one.
   */
  mapBounds?: [number, number, number, number] | null;
  promoted: boolean;
  promotedBy: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WellWorkspacesListResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: WellWorkspaceListItem[];
}

export interface WellWorkspacesSummary {
  total: number;
  last24Hours: number;
  last7Days: number;
  promoted: number;
}

// Story 1 wells-listing summary cards (POST /queries/wells/summary). Global DB counts.
export interface WellsSummary {
  total: number;
  last24Hours: number;
  last7Days: number;
  last24HourPermits: number;
}

// ── Add / remove wells (Epic 3, Story 3) ──
// POST/DELETE /workspaces/:id/wells answer with the workspace's wells in a deliberately minimal
// shape — enough to confirm what changed, not the full staged well _staged_well.json.jbuilder
// renders for the detail page. `id` is the staged-well id, which a removal can also be keyed by
// (the endpoint resolves an id OR a uwi). `manual` is the authored-vs-existing discriminator
// (`sw.new_well?`) and `source` the provenance string, together backing the Source column.
export interface WellsResultsRow {
  id: number;
  uwi: string;
  name: string | null;
  status: string | null;
  manual: boolean;
  source: string | null;
}

export interface WellsResultsResponse {
  results: WellsResultsRow[];
  /**
   * Existing-well uwis the add dropped because they resolved to no LIVE canonical well — bogus,
   * or soft-deleted between the catalog search and Save. The backend drops them rather than
   * failing the batch, so without reading this the well just vanishes without a word.
   *
   * Always sent (`[]` when nothing was skipped, and on every DELETE), and camelCase on the wire
   * because jbuilder camelizes every key — it is `skipped_uwis` in the partial.
   */
  skippedUwis: string[];
}

// One manual (authored) well as the add endpoint takes it: Well Name, Status, and — when the chosen
// status is a derived stage — its required lifecycle dates. snake_case, since only the RESPONSE
// passes through jbuilder's camelizer. UWI is backend-generated; a row left blank is seeded a
// numbered placeholder. A dateless stage (pre_permit / apd) sends no date keys at all; the server
// re-runs the same required-dates and ordering rules it runs for a Story-2 edit and 422s the whole
// batch if any row is short of them.
export interface ManualWellInput {
  lease_name: string;
  well_number: string;
  // Accepted by StagedWell::MANUAL_WELL_FIELDS: the Well Name parts (lease_name + well_number) and
  // the Well Status set only. current_operator, county, state, formation and the uwi are NOT in that
  // list — operator stays editable on the Story-2 edit surface, not on this manual add.
  status: WellStatusToken;
  permit_date?: string | null;
  spud_date?: string | null;
  rig_release_date?: string | null;
  completion_date?: string | null;
  first_production_date?: string | null;
}

// A row from POST /queries/wells (only the fields the listing + create modal use).
export interface WellQueryRow {
  uwi: string;
  name: string | null;
  currentOperator: string | null;
  holeDirection: string | null;
  status: string | null;
  // The state's DISPLAY name ("Texas"), not its code — `state_code` carries the two-letter
  // abbreviation and is a separate key on the same row.
  stateAbbreviation: string | null;
  // Already served by queries/wells/create.json.jbuilder:153; declared here for the Manage Wells
  // catalog table, which lists it beside the state the way the mock does.
  county: string | null;
  srpLandingZone: string | null;
  permitDate: string | null;
  spudDate: string | null;
  firstProductionDate: string | null;
  measuredDepthFeet: number | null;
  trueVerticalDepthFeet: number | null;
  href?: string;
}

export interface WellsQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: WellQueryRow[];
  // Present when the query asked for `include_filtered_uwis`: every well the filters
  // match, not just this page.
  filteredUwis?: { uwi: string; name: string | null }[];
}

// Wellbore geometry source (INS-11915): one geometry a bore can be driven from, as
// `POST /queries/wellbore_geometries` lists it (originals, promoted rows and the asking
// workspace's draft), already ordered for a radio group.
export interface WellboreGeometryListRow {
  /** Null on the filed survey, which is no geometry row and is chosen by clearing the pick. */
  id: number | null;
  auth: string[];
  uwi14: string;
  /** `original` is a vendor stick or legacy estimated bore that no workspace authored. */
  origin: 'original' | 'workspace';
  /** The authoring workspace's name; null on an original row. */
  name: string | null;
  workspaceId: number | null;
  source: string | null;
  geometrySource: string | null;
  boreType: string | null;
  /** Somebody's explicit pick. */
  isDefault: boolean;
  /** The row actually driving the bore right now. */
  isCurrent: boolean;
  promoted: boolean;
  promotedAt: string | null;
  /** The asking workspace's own un-promoted edit; a pick of it takes effect on promote. */
  draft: boolean;
  /** The pick is older than a geometry promoted since it was made. */
  supersededByNewer: boolean;
  targetZoneGeologicIntervalId: number | null;
  generatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: number | null; displayName: string };
  /** How a pick of this row is written: a default selection, or the clearing of one. */
  selectionMethod: 'select' | 'clear';
  /** The filed survey this edit was derived from has been re-filed since. */
  sourceSurveyChanged: boolean;
}

export interface WellboreGeometriesResponse {
  totalCount: number;
  results: WellboreGeometryListRow[];
}
